import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { weightUnits } from '../../../../../config/commons';
import { Store } from '../../../../../state/store.interfaces';
import { getUnit } from '../../../../Pdfs/TransferGraph/helpers';
import * as weighingTransfersSlice from '../../weighingTransfersSlice';
import { getModalWidth } from '../../../../OptimalHarvestPoint/helpers';
import { LrvText } from '../../../../../common/components/LrvText/LrvText';
import { LrvModal } from '../../../../../common/components/LrvModal/LrvModal';
import { TransferGraphD3 } from '../../../../Reports/TransferGraph/TransferGraphD3';

import styles from './TransferMetricChart.module.scss';

interface Props {
  theme?: 'dark' | 'light';
}

export const TransferMetricChart = (props: Props) => {
  const { theme = 'light' } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();

  const refChart = useRef<HTMLDivElement>(null);

  const [chart, setChart] = useState<TransferGraphD3 | null>(null);
  const { graphWidth, modalWidth, graphHeight } = getModalWidth();

  const {
    showChartModal,
    selectedTransferMetricEntry: transferMetrics,
    selectedParameter,
    capsules,
  } = useSelector((state: Store) => state.weighingTransfers);

  const { transfer } = capsules;

  useEffect(() => {
    if (!showChartModal || !refChart.current || !selectedParameter) {
      return;
    }

    if (chart) {
      chart.refreshChart({
        transferMetrics,
        height: graphHeight,
        width: graphWidth,
        parameter: selectedParameter,
      });
      return;
    }

    const newChart = new TransferGraphD3({
      container: refChart.current,
      transferMetrics,
      height: graphHeight,
      width: graphWidth,
      parameter: selectedParameter,
      circleRadioActive: 6,
      circleRadioDefault: 4,
    });
    setChart(newChart);
  }, [dispatch, transferMetrics, selectedParameter, graphWidth, graphHeight, refChart.current]);

  const onCancel = () => {
    setChart(null);
    dispatch(weighingTransfersSlice.setShowChartModal(false));
    dispatch(weighingTransfersSlice.setSelectedTransferMetricEntry([]));

    dispatch(weighingTransfersSlice.setShowHistogramModal(false));
  };

  const unit = getUnit({ parameter: `${selectedParameter}` });
  let title = `${t(`transfers.parameters.${selectedParameter}`)} (${unit})`;
  title = selectedParameter === 'animalsSmallerThan' ? `${t(`transfers.parameters.${selectedParameter}`, { value: transfer?.maxSmallAnimalsWeight })} ${weightUnits.G}` : title;

  return (
    <LrvModal
      theme={theme}
      title={title}
      open={showChartModal}
      forceRender={true}
      destroyOnClose={true}
      closable={false}
      onCancel={onCancel}
      className={styles.chartModal}
      width={modalWidth}
      okButtonProps={{ style: { display: 'none' } }}
    >
      <div className={styles.container}>
        <div
          id='transfer_metric_chart'
          ref={refChart}
          className={styles.chart}
        />

        <div className={styles.labelAxisX}>
          <LrvText text={t('transports.capsules')} theme={theme}/>
        </div>
      </div>
    </LrvModal>
  );
};
